import { Language } from "@generated/graphql";
import { Box, Button, Card, Stack, Typography } from "@mui/material";
import { palette } from "theme";
import { EVALUATION_TYPE_OPTIONS, LANGUAGE_BUTTONS } from "../../constants";
import { EvaluationType } from "../../types";

interface EvaluationTypeBlockProps {
  evaluationType: EvaluationType;
  estimatedDuration: number;
  indicatorPercent: number;
  language: Language;
  onChangeLanguage: (language: Language) => void;
}

const tariffColorMap: Record<EvaluationType, string> = {
  screening: "#39A76B",
  assessment: "#E0A722",
  extended: "#2D6CDF",
};

const zoneStyles = [
  { width: "16.7%", backgroundColor: "rgba(57, 167, 107, 0.28)" },
  { width: "33.3%", backgroundColor: "rgba(224, 167, 34, 0.28)" },
  { flex: 1, backgroundColor: "rgba(45, 108, 223, 0.22)" },
];

const EvaluationTypeBlock = ({
  evaluationType,
  estimatedDuration,
  indicatorPercent,
  language,
  onChangeLanguage,
}: EvaluationTypeBlockProps) => (
  <Card sx={{ p: 1.5, height: "100%" }}>
    <Stack spacing={1.5}>
      <Typography variant='h6'>Тип оценки</Typography>
      <Stack spacing={1}>
        {EVALUATION_TYPE_OPTIONS.map((item) => {
          const isActive = item.id === evaluationType;

          return (
            <Box
              key={item.id}
              sx={{
                px: 1.25,
                py: 1,
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: isActive ? "primary.main" : "divider",
                backgroundColor: isActive ? "rgba(45, 108, 223, 0.08)" : "background.paper",
                transition: "0.2s ease",
              }}>
              <Stack
                direction='row'
                justifyContent='space-between'
                spacing={1}>
                <Typography fontWeight={700}>{item.label}</Typography>
                <Typography
                  variant='body2'
                  color={isActive ? "primary.main" : "text.secondary"}>
                  {item.rangeLabel}
                </Typography>
              </Stack>
            </Box>
          );
        })}
      </Stack>
      <Stack spacing={0.75}>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='baseline'>
          <Typography
            variant='subtitle2'
            color='text.secondary'
            sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
            ~ Расчётное время
          </Typography>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
            <Typography
              variant='h5'
              fontWeight={700}>
              ~{estimatedDuration}
            </Typography>
            <Typography
              variant='body2'
              color='text.secondary'>
              мин
            </Typography>
          </Box>
        </Stack>
        <Box
          sx={{
            position: "relative",
            height: 10,
            borderRadius: 999,
            overflow: "hidden",
            backgroundColor: "divider",
          }}>
          <Stack
            direction='row'
            sx={{
              position: "absolute",
              inset: 0,
            }}>
            {zoneStyles.map((item) => (
              <Box
                key={`${item.width || "flex"}-${item.backgroundColor}`}
                sx={item}
              />
            ))}
          </Stack>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              width: `${indicatorPercent}%`,
              maxWidth: "100%",
              borderRadius: 999,
              backgroundColor: tariffColorMap[evaluationType],
              transition: "width 0.2s ease",
            }}
          />
        </Box>
        <Stack
          direction='row'
          justifyContent='space-between'>
          {["0", "30", "90", "180 мин"].map((label) => (
            <Typography
              key={label}
              variant='caption'
              color='text.secondary'>
              {label}
            </Typography>
          ))}
        </Stack>
      </Stack>
      <Stack spacing={1}>
        <Typography variant='subtitle2'>Язык генерации</Typography>
        <Stack
          direction='row'
          spacing={1}>
          {LANGUAGE_BUTTONS.map((item) => {
            const isActive = language === item.id;

            return (
              <Button
                key={item.id}
                color={isActive ? "third" : "secondary"}
                variant={isActive ? "contained" : "outlined"}
                onClick={() => onChangeLanguage(item.id)}
                title={item.description}
                size='small'
                sx={{
                  fontWeight: 500,
                  borderWidth: 1,
                  borderStyle: "solid",
                  borderColor: isActive ? palette.primary.main : palette.border,
                }}>
                {item.label}
              </Button>
            );
          })}
        </Stack>
        <Typography
          variant='body2'
          color='text.secondary'>
          {LANGUAGE_BUTTONS.find((item) => item.id === language)?.description} — язык вопросов и критериев
        </Typography>
      </Stack>
    </Stack>
  </Card>
);

export default EvaluationTypeBlock;
